import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Plus, Edit2, Upload, Trash2 } from 'lucide-react'
import { usersApi, type UserUpsertPayload } from '../../api/users'
import { useAuthStore } from '../../store/authStore'
import { Avatar } from '../../components/common/Avatar'
import { Button } from '../../components/common/Button'
import { Modal } from '../../components/common/Modal'
import { ALL_ROLES, canEditUsers, canManageSensitiveUserFields, formatRoleLabel } from '../../lib/roles'
import { invalidateUserPresentationViews } from '../../lib/queryInvalidation'
import type { User, UserRole } from '../../types'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: '14px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  outline: 'none',
  boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  marginBottom: '4px',
}

interface UserFormData {
  fullName: string
  email: string
  role: User['role']
  password: string
  isActive: boolean
  canSubmitExpenses: boolean
  avatarFile: File | null
  removeAvatar: boolean
}

function UserFormModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  initialData,
  isEdit,
  roleOptions,
  canManageSensitive,
}: {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: UserUpsertPayload) => void
  isSubmitting: boolean
  initialData?: User
  isEdit: boolean
  roleOptions: UserRole[]
  canManageSensitive: boolean
}) {
  const [form, setForm] = useState<UserFormData>({
    fullName: initialData?.fullName || '',
    email: initialData?.email || '',
    role: initialData?.role || 'DEVELOPER',
    password: '',
    isActive: initialData?.isActive ?? true,
    canSubmitExpenses: initialData?.canSubmitExpenses ?? false,
    avatarFile: null,
    removeAvatar: false,
  })

  useEffect(() => {
    if (!isOpen) return
    setForm({
      fullName: initialData?.fullName || '',
      email: initialData?.email || '',
      role: initialData?.role || 'DEVELOPER',
      password: '',
      isActive: initialData?.isActive ?? true,
      canSubmitExpenses: initialData?.canSubmitExpenses ?? false,
      avatarFile: null,
      removeAvatar: false,
    })
  }, [initialData, isOpen])

  const avatarPreview = useMemo(() => {
    if (form.avatarFile) return URL.createObjectURL(form.avatarFile)
    if (!form.removeAvatar) return initialData?.avatarUrl || null
    return null
  }, [form.avatarFile, form.removeAvatar, initialData?.avatarUrl])

  useEffect(() => {
    return () => {
      if (form.avatarFile && avatarPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview)
      }
    }
  }, [avatarPreview, form.avatarFile])

  const set = (key: keyof UserFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({
      ...p,
      [key]:
        key === 'isActive'
          ? (e.target as HTMLInputElement).checked
          : e.target.value,
    }))

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    setForm((prev) => ({
      ...prev,
      avatarFile: file,
      removeAvatar: false,
    }))
  }

  const handleSubmit = () => {
    const data: UserUpsertPayload = {
      fullName: form.fullName.trim(),
      email: form.email.trim().toLowerCase(),
      role: form.role,
      avatar: form.avatarFile,
      removeAvatar: form.removeAvatar,
      canSubmitExpenses: form.canSubmitExpenses,
    }

    if (!isEdit) {
      data.password = form.password
      data.isActive = form.isActive
    } else {
      if (form.password) data.password = form.password
      data.isActive = form.isActive
    }

    onSubmit(data)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit User' : 'Create User'} width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <Avatar
            user={{ fullName: form.fullName || initialData?.fullName || 'User', avatarUrl: avatarPreview || undefined }}
            size="lg"
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={labelStyle}>Profile Photo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <label
                style={{
                  ...inputStyle,
                  width: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                }}
              >
                <Upload size={14} />
                <span>{form.avatarFile ? 'Change Photo' : 'Upload Photo'}</span>
                <input type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleAvatarChange} style={{ display: 'none' }} />
              </label>
              {(avatarPreview || initialData?.avatarUrl) && (
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, avatarFile: null, removeAvatar: true }))}
                  style={{
                    border: '1px solid #fecaca',
                    background: '#fef2f2',
                    color: '#dc2626',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={14} />
                  Remove
                </button>
              )}
            </div>
            <span style={{ fontSize: '12px', color: '#64748b' }}>PNG, JPG, GIF, or WEBP up to 5MB.</span>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Full Name *</label>
          <input value={form.fullName} onChange={set('fullName')} style={inputStyle} placeholder="Jane Smith" />
        </div>

        <div>
          <label style={labelStyle}>Email *</label>
          <input type="email" value={form.email} onChange={set('email')} style={inputStyle} placeholder="jane@company.com" />
        </div>

        {!isEdit && (
          <div>
            <label style={labelStyle}>Password *</label>
            <input type="password" value={form.password} onChange={set('password')} style={inputStyle} placeholder="Min. 8 characters" />
          </div>
        )}

        <div>
          <label style={labelStyle}>Role *</label>
          <select value={form.role} onChange={set('role')} style={inputStyle}>
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {formatRoleLabel(r)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Account Status</label>
          <select
            value={form.isActive ? 'active' : 'inactive'}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                isActive: e.target.value === 'active',
              }))
            }
            style={inputStyle}
          >
            <option value="active">Active</option>
            <option value="inactive">Archived / Inactive</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={labelStyle}>Accounting Access</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
            <input
              type="checkbox"
              checked={form.canSubmitExpenses}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  canSubmitExpenses: e.target.checked,
                }))
              }
            />
            Allow this user to access Accounting
          </label>
        </div>

        {isEdit && canManageSensitive && (
          <div>
            <label style={labelStyle}>Reset Password</label>
            <input
              type="password"
              value={form.password}
              onChange={set('password')}
              style={inputStyle}
              placeholder="Leave blank to keep current password"
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={isSubmitting}
            disabled={!form.fullName.trim() || !form.email.trim() || (!isEdit && !form.password)}
            onClick={handleSubmit}
          >
            {isEdit ? 'Save Changes' : 'Create User'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const roleBadge = (role: User['role']) => {
  const colors: Record<string, { bg: string; color: string }> = {
    SUPER_ADMIN: { bg: '#fef2f2', color: '#991b1b' },
    ADMIN: { bg: '#fef2f2', color: '#dc2626' },
    MANAGER: { bg: '#fff7ed', color: '#c2410c' },
    LEADER: { bg: '#ecfdf5', color: '#047857' },
    TESTER: { bg: '#f5f3ff', color: '#6d28d9' },
    DEVELOPER: { bg: '#eff6ff', color: '#2563eb' },
    CLIENT: { bg: '#f0fdf4', color: '#16a34a' },
  }
  const c = colors[role] || { bg: '#f1f5f9', color: '#475569' }
  return (
    <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: c.bg, color: c.color }}>
      {formatRoleLabel(role)}
    </span>
  )
}

const th: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 600,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
}

const td: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'middle',
}

const noticeStyle = (tone: 'success' | 'warning' | 'error'): React.CSSProperties => {
  const colors = {
    success: { bg: '#ecfdf3', border: '#bbf7d0', color: '#166534' },
    warning: { bg: '#fffbeb', border: '#fde68a', color: '#92400e' },
    error: { bg: '#fef2f2', border: '#fecaca', color: '#991b1b' },
  }[tone]

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 12px',
    marginBottom: '14px',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    background: colors.bg,
    color: colors.color,
    fontSize: '13px',
  }
}

const noticeCloseStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '12px',
}

export default function UsersPage() {
  const qc = useQueryClient()
  const { user: currentUser } = useAuthStore()
  const [createOpen, setCreateOpen] = useState(false)
  const [editUser, setEditUser] = useState<User | null>(null)
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning' | 'error'; message: string } | null>(null)
  const canEditDirectory = canEditUsers(currentUser?.role)
  const canManageSensitive = canManageSensitiveUserFields(currentUser?.role)
  const roleOptions = canManageSensitive
    ? ALL_ROLES
    : ALL_ROLES.filter((role) => role !== 'SUPER_ADMIN')

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.listUsers(),
  })

  const createMutation = useMutation({
    mutationFn: (data: UserUpsertPayload) => usersApi.createUser(data),
    onSuccess: async (res) => {
      await invalidateUserPresentationViews(qc)
      setCreateOpen(false)
      setNotice({
        tone: res.data.email?.sent === false ? 'warning' : 'success',
        message: res.data.email?.message || 'User created successfully.',
      })
    },
    onError: (error: any) => {
      setNotice({
        tone: 'error',
        message: error.response?.data?.error || 'Could not create user.',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpsertPayload }) => usersApi.updateUser(id, data),
    onSuccess: async () => {
      await invalidateUserPresentationViews(qc)
      setEditUser(null)
      setNotice({ tone: 'success', message: 'User updated successfully.' })
    },
    onError: (error: any) => {
      setNotice({
        tone: 'error',
        message: error.response?.data?.error || 'Could not update user.',
      })
    },
  })

  const users: User[] = (data?.data?.data || []).filter((user) => user.isActive)

  return (
    <div style={{ padding: '24px', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Users</h1>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>{users.length} active accounts</p>
        </div>
        {canEditDirectory && (
          <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus size={14} /> New User
          </Button>
        )}
      </div>

      {notice && (
        <div style={noticeStyle(notice.tone)}>
          <span>{notice.message}</span>
          <button onClick={() => setNotice(null)} style={noticeCloseStyle}>Dismiss</button>
        </div>
      )}

      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>User</th>
              <th style={th}>Role</th>
              <th style={th}>Expenses</th>
              <th style={th}>Status</th>
              <th style={th}>Joined</th>
              {canEditDirectory && <th style={{ ...th, width: '60px' }}></th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={canEditDirectory ? 6 : 5} style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>Loading...</td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id}>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Avatar user={u} size="sm" />
                      <div>
                        <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#1e293b' }}>{u.fullName}</div>
                        <div style={{ fontSize: '12px', color: '#94a3b8' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={td}>{roleBadge(u.role)}</td>
                  <td style={td}>
                    <span
                      style={{
                        fontSize: '12px',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        background: u.canSubmitExpenses ? '#eff6ff' : '#f8fafc',
                        color: u.canSubmitExpenses ? '#1d4ed8' : '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {u.canSubmitExpenses ? 'Allowed' : 'No access'}
                    </span>
                  </td>
                  <td style={td}>
                    <span
                      style={{
                        fontSize: '12px',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        background: u.isActive ? '#f0fdf4' : '#fef2f2',
                        color: u.isActive ? '#16a34a' : '#dc2626',
                        fontWeight: 500,
                      }}
                    >
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ ...td, fontSize: '12px', color: '#64748b' }}>
                    {format(new Date(u.createdAt), 'MMM d, yyyy')}
                  </td>
                  {canEditDirectory && (
                    <td style={td}>
                      <button
                        onClick={() => setEditUser(u)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', padding: '4px', borderRadius: '4px' }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = '#6366f1')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
                      >
                        <Edit2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: '12px', color: '#64748b', margin: '12px 4px 0' }}>
        Admins can add people, update email and role labels, decide who can access Accounting, upload profile photos, and archive accounts when needed. Users can manage their own profile photo from the sidebar account area.
      </p>

      {canEditDirectory && (
        <UserFormModal
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          onSubmit={(payload) => createMutation.mutate(payload)}
          isSubmitting={createMutation.isPending}
          isEdit={false}
          roleOptions={roleOptions}
          canManageSensitive={canManageSensitive}
        />
      )}

      {editUser && canEditDirectory && (
        <UserFormModal
          isOpen={!!editUser}
          onClose={() => setEditUser(null)}
          onSubmit={(payload) => updateMutation.mutate({ id: editUser.id, data: payload })}
          isSubmitting={updateMutation.isPending}
          initialData={editUser}
          isEdit
          roleOptions={roleOptions}
          canManageSensitive={canManageSensitive}
        />
      )}
    </div>
  )
}
